import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Upload, FileText, X } from '@phosphor-icons/react';
import { fmtDateFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Section affichee dans l'onglet Gestion du chantier :
 *  - Retenue de garantie : montant a recuperer, date liberation, rappel auto
 *  - Caution bancaire : upload attestation
 *  - Bon de commande : upload PDF + numero
 *  - Compte prorata : appels de fonds
 */
export const ProjectGuaranteeSection = ({ project, onChange }) => {
  const [calls, setCalls] = useState([]);
  const [newCall, setNewCall] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', note: '' });
  const [releaseDate, setReleaseDate] = useState(project?.retention_release_date || '');
  const [uploading, setUploading] = useState(null); // 'po' | 'caution'

  const fetchCalls = useCallback(async () => {
    if (!project?.id) return;
    try {
      const r = await axios.get(`${API_URL}/api/projects/${project.id}/prorata-calls`, { withCredentials: true });
      setCalls(r.data || []);
    } catch (e) { /* noop */ }
  }, [project?.id]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);
  useEffect(() => { setReleaseDate(project?.retention_release_date || ''); }, [project?.retention_release_date]);

  const guarantee = project?.guarantee_type;
  const retentionPct = Number(project?.retention_percent || 0);
  const totalRetention = Number(project?.total_retention_ht || 0);
  const contractType = project?.contract_type || 'direct';
  const poNumber = project?.purchase_order_number || '';
  const poDocId = project?.purchase_order_doc_id || '';
  const cautionDocId = project?.caution_attestation_doc_id || '';

  const saveReleaseDate = async () => {
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}`, { retention_release_date: releaseDate || null }, { withCredentials: true });
      toast.success('Date de liberation mise a jour (rappel auto-cree 15 j avant)');
      onChange?.();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const addCall = async () => {
    const amt = parseFloat(newCall.amount);
    if (!amt || amt <= 0) return toast.error('Montant requis');
    try {
      await axios.post(`${API_URL}/api/projects/${project.id}/prorata-calls`, newCall, { withCredentials: true });
      toast.success('Appel de fonds ajoute');
      setNewCall({ date: new Date().toISOString().slice(0, 10), amount: '', note: '' });
      fetchCalls();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const removeCall = async (id) => {
    if (!window.confirm('Supprimer cet appel de fonds ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/prorata-calls/${id}`, { withCredentials: true });
      toast.success('Supprime');
      fetchCalls();
      onChange?.();
    } catch { toast.error('Erreur'); }
  };

  const uploadDoc = async (file, kind /* 'po' | 'caution' */) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', kind === 'po' ? `Bon de commande - ${project.name}` : `Attestation de caution - ${project.name}`);
      fd.append('category', kind === 'po' ? 'bon_commande' : 'caution');
      const r = await axios.post(`${API_URL}/api/projects/${project.id}/documents`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const updateField = kind === 'po' ? 'purchase_order_doc_id' : 'caution_attestation_doc_id';
      await axios.put(`${API_URL}/api/projects/${project.id}`, { [updateField]: r.data.id }, { withCredentials: true });
      toast.success(kind === 'po' ? 'Bon de commande enregistre' : 'Attestation enregistree');
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur upload'); }
    setUploading(null);
  };

  const replaceDoc = (kind) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/*';
    input.onchange = (e) => { if (e.target.files?.[0]) uploadDoc(e.target.files[0], kind); };
    input.click();
  };

  const totalProrataCalls = calls.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const prorataBudget = Number(project?.prorata_amount || 0);

  return (
    <div data-testid="project-guarantee-section" style={{ marginBottom: 18, display: 'grid', gap: 14 }}>
      {/* --- BLOC RETENUE / CAUTION --- */}
      {guarantee === 'retenue' && (
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #F97316' }} data-testid="retention-block">
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#9A3412' }}>Retenue de garantie ({retentionPct}%)</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, alignItems: 'end' }}>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase', fontWeight: 700 }}>Montant a recuperer</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#9A3412' }}>{totalRetention.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR HT</div>
              <div style={{ fontSize: '0.72rem', color: '#7A7B82', marginTop: 4 }}>Somme des {retentionPct}% deduits sur toutes les factures emises de ce chantier.</div>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date de liberation</label>
              <input type="date" className="form-input" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} data-testid="retention-release-date-input" />
            </div>
            <div>
              <button className="btn btn-primary" onClick={saveReleaseDate} data-testid="retention-save-date-btn">Enregistrer la date</button>
              <div style={{ fontSize: '0.7rem', color: '#5E5F66', marginTop: 4 }}>Un rappel sera cree 15 jours avant.</div>
            </div>
          </div>
        </div>
      )}
      {guarantee === 'caution' && (
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #DC2626' }} data-testid="caution-block">
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#991B1B' }}>Caution bancaire</h4>
          {cautionDocId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#E8F5E9', color: '#2E7D32', borderRadius: 6, fontWeight: 700, fontSize: '0.85rem' }}>
                <FileText size={16} /> Attestation enregistree
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => replaceDoc('caution')} disabled={uploading === 'caution'} data-testid="caution-replace-btn">
                <Upload size={14} /> {uploading === 'caution' ? '...' : 'Remplacer'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: '#991B1B', fontWeight: 700 }}>Aucune attestation. A ajouter !</span>
              <button className="btn btn-primary btn-sm" onClick={() => replaceDoc('caution')} disabled={uploading === 'caution'} data-testid="caution-upload-btn">
                <Upload size={14} /> {uploading === 'caution' ? '...' : "Ajouter l'attestation"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* --- BLOC BON DE COMMANDE --- */}
      {contractType === 'bon_commande' && (
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #F59E0B' }} data-testid="bon-commande-block">
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#92400E' }}>Bon de commande</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, alignItems: 'end' }}>
            <div>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Numero du bon</label>
              <input type="text" className="form-input" defaultValue={poNumber} onBlur={async (e) => {
                if (e.target.value === poNumber) return;
                try {
                  await axios.put(`${API_URL}/api/projects/${project.id}`, { purchase_order_number: e.target.value }, { withCredentials: true });
                  toast.success('N° bon de commande sauvegarde');
                  onChange?.();
                } catch { toast.error('Erreur'); }
              }} placeholder="BC-2026-001" data-testid="po-number-input" />
            </div>
            <div>
              {poDocId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#E8F5E9', color: '#2E7D32', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem' }}>
                    <FileText size={14} /> PDF joint
                  </span>
                  <button className="btn btn-secondary btn-sm" onClick={() => replaceDoc('po')} disabled={uploading === 'po'} data-testid="po-doc-replace-btn">
                    <Upload size={12} /> {uploading === 'po' ? '...' : 'Remplacer'}
                  </button>
                </div>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => replaceDoc('po')} disabled={uploading === 'po'} data-testid="po-doc-upload-btn">
                  <Upload size={14} /> {uploading === 'po' ? '...' : 'Joindre le PDF du bon'}
                </button>
              )}
              <div style={{ fontSize: '0.7rem', color: '#92400E', marginTop: 4 }}>Le PDF du bon sera annexe automatiquement a chaque facture editee.</div>
            </div>
          </div>
        </div>
      )}

      {/* --- BLOC PRORATA --- */}
      {project?.prorata_enabled && (
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #10B981' }} data-testid="prorata-block">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#065F46' }}>Compte prorata ({Number(project?.prorata_percent || 0)}% du marche - {prorataBudget.toLocaleString('fr-FR')} EUR)</h4>
            <span style={{ fontSize: '0.85rem', color: '#065F46' }}>
              Cumul des appels : <strong>{totalProrataCalls.toLocaleString('fr-FR')} EUR</strong> · Reste : <strong>{(prorataBudget - totalProrataCalls).toLocaleString('fr-FR')} EUR</strong>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 160px 1fr auto', gap: 8, alignItems: 'end', marginBottom: 10 }}>
            <div>
              <label className="form-label" style={{ fontSize: '0.72rem' }}>Date</label>
              <input type="date" className="form-input" value={newCall.date} onChange={(e) => setNewCall({ ...newCall, date: e.target.value })} data-testid="prorata-new-date" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.72rem' }}>Montant (EUR)</label>
              <input type="number" step="0.01" className="form-input" value={newCall.amount} onChange={(e) => setNewCall({ ...newCall, amount: e.target.value })} placeholder="0.00" data-testid="prorata-new-amount" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.72rem' }}>Note</label>
              <input type="text" className="form-input" value={newCall.note} onChange={(e) => setNewCall({ ...newCall, note: e.target.value })} placeholder="Note (optionnel)" data-testid="prorata-new-note" />
            </div>
            <button className="btn btn-primary" onClick={addCall} data-testid="prorata-add-btn"><Plus size={14} /> Ajouter</button>
          </div>
          {calls.length > 0 && (
            <table className="data-table" style={{ fontSize: '0.85rem' }}>
              <thead><tr><th>Date</th><th>Montant</th><th>Note</th><th></th></tr></thead>
              <tbody>
                {calls.map(c => (
                  <tr key={c.id} data-testid={`prorata-call-${c.id}`}>
                    <td>{fmtDateFR(c.date)}</td>
                    <td style={{ fontWeight: 700, color: '#065F46' }}>{Number(c.amount).toLocaleString('fr-FR')} EUR</td>
                    <td>{c.note || '-'}</td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => removeCall(c.id)} data-testid={`prorata-del-${c.id}`}><Trash size={13} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectGuaranteeSection;
